/**
 * Marketing-site constants shared by the apex landing page and every
 * public marketing route (/customers, /faq, /compare/*, /booking-software/*).
 *
 * The apex marketing site lives at askflow.sa while `NEXT_PUBLIC_SITE_URL`
 * (appConfig.url) points at the flow.askai.sa booking deployment, so
 * marketing pages resolve canonicals/OG against this constant, never
 * appConfig.url.
 */
export const MARKETING_SITE_URL =
  process.env.NEXT_PUBLIC_MARKETING_SITE_URL ?? 'https://askflow.sa';

/**
 * Former apex domains that still serve the marketing site as a backup.
 * They answer with the marketing sitemap, but every canonical points at
 * MARKETING_SITE_URL so search engines consolidate on the current domain.
 */
export const LEGACY_MARKETING_HOSTS = ['askai.sa', 'www.askai.sa'] as const;

export const MARKETING_SITE_NAME = 'Flow';

export const MARKETING_ORG_NAME = 'ASK AI';

export const BOOKING_APP_URL = 'https://flow.askai.sa';

export const CONTACT_EMAIL = 'hello@askai.sa';

export const INSTAGRAM_URL = 'https://www.instagram.com/askflow.sa';

/**
 * Rounded platform aggregates used for marketing social proof.
 * Snapshotted from production on 2026-08-05. Round DOWN so the claims
 * only ever get truer. Update deliberately, not automatically.
 */
export const PLATFORM_STATS = {
  venues: '25+',
  locations: '40+',
  courtsAndSpaces: '190+',
  bookings: '35,000+',
  players: '28,000+',
  countries: 3,
  whatsappMessages: '100,000+',
} as const;

/**
 * ISO date stamped into JSON-LD as dateModified. AI answer engines weight
 * freshness heavily, so bump this whenever marketing copy meaningfully
 * changes (new pages, rewritten sections, updated stats) — not on
 * cosmetic tweaks, and never automatically.
 */
export const MARKETING_CONTENT_UPDATED = '2026-09-27';

export type MarketingLocale = 'en' | 'ar';

export function asMarketingLocale(language: string): MarketingLocale {
  return language === 'ar' ? 'ar' : 'en';
}

/** Absolute URL for a marketing path, e.g. marketingUrl('/faq'). */
export function marketingUrl(path: string): string {
  return `${MARKETING_SITE_URL}${path === '/' ? '' : path}`;
}
